// Reset password pages — shopify sends a URL to the user with a complicated path
import { useState } from 'react'

import Link from 'next/link'

import { useForm } from 'react-hook-form'

import AuthTextNodes from '@data/i18n/auth.json'

import fetcher from '@lib/fetcher'
import customerResetMutation from '@lib/mutations/customer-reset'

import PasswordInput from '@components/form-controls/PasswordInput'

interface IFormInput {
	password: string
	signInError: string
	passwordConfirm: string
	errorOther?: string
}

export default function ResetPasswordScreen({
	onSubmit
}: {
	onSubmit: (data: IFormInput) => Promise<{ customerUserErrors: any[] }>
}) {
	const {
		register,
		handleSubmit,
		watch,
		setError,
		clearErrors,
		formState: { errors }
	} = useForm<IFormInput>()

	const [complete, setComplete] = useState(false)

	const password = watch('password')

	const onResetSubmit = async (data: IFormInput) => {
		clearErrors()

		try {
			const res = await onSubmit(data)

			if (res.customerUserErrors.length > 0) {
				setError('errorOther', {
					message: res.customerUserErrors[0].message
				})
				console.log(res)
			} else {
				setComplete(true)
			}
		} catch (error) {
			setError('errorOther', {
				message: 'Something went wrong. Please try again.'
			})
			console.log(error)
		}
	}
	return (
		<div className="py-14">
			{complete ? (
				<div className="flex flex-col max-w-md mx-auto px-8 gap-y-4">
					<h1 className="text-3xl text-center">{AuthTextNodes.resetPassword.success}</h1>
					<p className="mt-4 mb-10 font-medium text-center text-gray-500 max-md:text-sm">
						{AuthTextNodes.resetPassword.passwordUpdated}
					</p>

					<Link legacyBehavior href="/profile/subscriptions" prefetch={false}>
						<a className="button button-primary">{AuthTextNodes.login.cta}</a>
					</Link>
				</div>
			) : (
				<form
					onSubmit={handleSubmit(onResetSubmit)}
					className="flex flex-col max-w-md mx-auto px-8 gap-y-4"
				>
					<h1 className="text-3xl text-center">{AuthTextNodes.resetPassword.cta}</h1>

					{/* Description */}
					<p className="mt-4 mb-10 font-medium text-center text-gray-500 max-md:text-sm">
						{AuthTextNodes.resetPassword.loginProfile}
					</p>

					{errors.errorOther && (
						<p className="p-2 bg-red-100 ">
							{errors.errorOther.message}.
							<br /> <br />{' '}
							<span className="text-sm">
								If you&apos;re activating your account for the first time, please try again later,
								or contact us at{' '}
								<a className="underline" href="mailto:info@birchbox.com">
									info@birchbox.com
								</a>{' '}
								if the problem persists.
							</span>
						</p>
					)}
					<PasswordInput register={register} canToggleVisible />
					<PasswordInput
						register={register}
						errors={errors.passwordConfirm?.message}
						id="passwordConfirm"
						placeholder="Password Confirmation"
						canToggleVisible
						validate={(value: string) => value === password || 'Passwords do not match'}
					/>

					<button className="button button-primary" type="submit">
						{AuthTextNodes.resetPassword.submit}
					</button>
				</form>
			)}
		</div>
	)
}
